import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { API } from '../api';
import { SeverityBadge } from './Badge';
import { INPUT_RING } from './FilterBar';
import { cvePath, hostPath, packagePath } from './entityRoutes';
import { formatCount, plural } from './format';

const MIN_CHARS = 2;
const LIMIT = 5;
const DEBOUNCE_MS = 250;
const CVE_ID = /^cve-\d{4}-\d{4,}$/i;
const LIST_ID = 'global-search-results';

const isTypingTarget = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

// Each group is one list endpoint filtered by `kw`; the API matches CVE ID, package name and hostname.
const GROUPS = [
  {
    key: 'cve',
    title: 'Vulnerabilities',
    noun: ['vulnerability', 'vulnerabilities'],
    load: (kw) => API.GET_VULNS({ kw, limit: LIMIT, track: false }),
    listPath: (kw) => `/vulnerabilities?kw=${encodeURIComponent(kw)}`,
    rowId: (r) => r.cve,
    rowPath: (r) => cvePath(r.cve),
  },
  {
    key: 'package',
    title: 'Packages',
    noun: ['package', 'packages'],
    load: (kw) => API.GET_PACKAGES({ kw, limit: LIMIT, track: false }),
    listPath: (kw) => `/packages?kw=${encodeURIComponent(kw)}`,
    rowId: (r) => r.id,
    rowPath: (r) => packagePath({ id: r.id, name: r.name }),
  },
  {
    key: 'host',
    title: 'Hosts',
    noun: ['host', 'hosts'],
    load: (kw) => API.GET_HOSTS({ kw, limit: LIMIT, track: false }),
    listPath: (kw) => `/hosts?kw=${encodeURIComponent(kw)}`,
    rowId: (r) => r.id,
    rowPath: (r) => hostPath({ id: r.id, name: r.hostname }),
  },
];

/**
 * Debounced search across the three groups. Returns { [groupKey]: { term, rows, total } | { term, error } };
 * an entry whose term differs from the current one is still loading. A CVE ID searches vulnerabilities only.
 */
function useEntitySearch(term) {
  const [results, setResults] = useState({});
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (term.length < MIN_CHARS) {
      setResults({});
      return undefined;
    }
    let live = true;
    const groups = CVE_ID.test(term) ? GROUPS.filter((g) => g.key === 'cve') : GROUPS;
    const timer = setTimeout(() => {
      groups.forEach((group) => {
        group.load(term).then(
          (res) => live && setResults((prev) => ({ ...prev, [group.key]: { term, rows: res?.data || [], total: res?.total ?? 0 } })),
          (error) => live && setResults((prev) => ({ ...prev, [group.key]: { term, error } })),
        );
      });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [term, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { results, retry };
}

function Match({ text, term }) {
  const value = String(text ?? '');
  const at = term ? value.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (at < 0) return value;
  return (
    <>
      {value.slice(0, at)}
      <mark className="bg-transparent text-inherit font-semibold">{value.slice(at, at + term.length)}</mark>
      {value.slice(at + term.length)}
    </>
  );
}

const Meta = ({ children, className = 'text-graphite' }) => (
  <span className={`w-[124px] shrink-0 text-right text-small tabular-nums whitespace-nowrap ${className}`}>{children}</span>
);

function CveRow({ row, term }) {
  return (
    <>
      <span className="w-[136px] shrink-0 truncate font-mono text-id text-ink">
        <Match text={row.cve} term={term} />
      </span>
      <span className="w-[68px] shrink-0">
        <SeverityBadge severity={row.severity} />
      </span>
      <span className="flex-1 min-w-0 truncate text-graphite">{row.description || row.vuln_description}</span>
      <Meta>{plural(row.affected_host_count, 'host')}</Meta>
    </>
  );
}

function PackageRow({ row, term }) {
  return (
    <>
      <span className="flex-1 min-w-0 truncate font-mono text-id">
        <span className="text-ink">
          <Match text={row.name} term={term} />
        </span>
        {row.version && <span className="ml-1.5 text-graphite">{row.version}</span>}
      </span>
      <Meta>{plural(row.host_count, 'host')}</Meta>
      <Meta>{plural(row.vuln_count, 'vulnerability', 'vulnerabilities')}</Meta>
    </>
  );
}

function HostRow({ row, term }) {
  const os = [row.os_name, row.os_version].filter(Boolean).join(' ');
  return (
    <>
      <span className="flex-1 min-w-0 truncate font-mono text-id text-ink">
        <Match text={row.hostname} term={term} />
      </span>
      <span className="w-[120px] shrink-0 truncate text-small text-graphite">{os}</span>
      <Meta>{plural(row.active_vuln_count, 'vulnerability', 'vulnerabilities')}</Meta>
      <Meta className="text-crit">{row.critical_vuln_count > 0 ? `${formatCount(row.critical_vuln_count)} critical` : ''}</Meta>
    </>
  );
}

const ROWS = { cve: CveRow, package: PackageRow, host: HostRow };

const Kbd = ({ children }) => (
  <kbd className="font-mono text-small leading-none px-1.5 py-0.5 rounded-badge border border-rule text-graphite">{children}</kbd>
);

/**
 * Top-bar search. `/` or Cmd/Ctrl+K focuses it; results for CVEs, packages and hosts open below the
 * input and link to the entity pages. A full CVE ID opens the CVE page on Enter without waiting.
 * Requests skip the global loading indicator and the export dialog's "current page" query.
 */
export default function GlobalSearch() {
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const term = query.trim();
  const cveId = CVE_ID.test(term) ? term.toUpperCase() : null;
  const { results, retry } = useEntitySearch(term);

  const groups = useMemo(() => {
    if (term.length < MIN_CHARS) return [];
    return GROUPS.filter((g) => !cveId || g.key === 'cve').map((group) => {
      const result = results[group.key];
      const current = result?.term === term;
      let rows = result?.rows || [];
      if (cveId) rows = rows.filter((r) => String(r.cve).toUpperCase() !== cveId);
      return { group, rows, total: result?.total ?? 0, error: current ? result.error : null, loading: !current };
    });
  }, [results, term, cveId]);

  const exactCve = cveId ? (results.cve?.rows || []).find((r) => String(r.cve).toUpperCase() === cveId) : null;

  // Flat option list in display order, so arrow keys walk across groups.
  const options = useMemo(() => {
    const list = [];
    if (cveId) list.push({ id: 'gs-cve-jump', to: cvePath(cveId) });
    groups.forEach(({ group, rows, total, error, loading }) => {
      if (error || loading) return;
      rows.forEach((row) => list.push({ id: `gs-${group.key}-${group.rowId(row)}`, to: group.rowPath(row) }));
      if (!cveId && total > rows.length) list.push({ id: `gs-${group.key}-all`, to: group.listPath(term) });
    });
    return list;
  }, [groups, cveId, term]);

  const activeIndex = Math.min(active, Math.max(options.length - 1, 0));
  const activeId = open ? options[activeIndex]?.id : undefined;
  const panelOpen = open && term.length > 0;

  useEffect(() => setActive(0), [term]);
  useEffect(() => setOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (document.querySelector('[aria-modal="true"]')) return;
      const isSlash = event.key === '/' && !isTypingTarget(document.activeElement);
      const isCmdK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
      if (isSlash || isCmdK) {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const finish = () => {
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const go = (to) => {
    history.push(to);
    finish();
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (panelOpen) setOpen(false);
      else inputRef.current.blur();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[activeIndex];
      if (panelOpen && option) go(option.to);
      else if (cveId) go(cvePath(cveId));
      return;
    }
    if (!panelOpen || options.length === 0) return;
    if (event.key === 'ArrowDown') setActive((activeIndex + 1) % options.length);
    else if (event.key === 'ArrowUp') setActive((activeIndex - 1 + options.length) % options.length);
    else return;
    event.preventDefault();
  };

  const optionProps = (id, to) => {
    const index = options.findIndex((o) => o.id === id);
    return {
      id,
      to,
      role: 'option',
      tabIndex: -1,
      'aria-selected': index === activeIndex,
      onMouseMove: () => index !== activeIndex && setActive(index),
      onClick: (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        finish();
      },
      className: [
        'flex items-center gap-3 h-9 mx-1 px-2 rounded-control text-body outline-none',
        index === activeIndex ? 'bg-signal-soft shadow-[inset_2px_0_0_rgb(var(--signal))]' : '',
      ].join(' '),
    };
  };

  const settled = groups.length > 0 && groups.every((g) => !g.loading);
  const searching = groups.some((g) => g.loading);
  const nothingFound = settled && !cveId && groups.every((g) => !g.error && g.rows.length === 0);
  const blank = searching && !cveId && groups.every((g) => !g.error && g.rows.length === 0);

  return (
    <div className="relative w-full max-w-[460px]">
      <label className={`flex items-center gap-2 h-8 px-2.5 rounded-control border border-rule bg-paper text-graphite focus-within:border-signal ${INPUT_RING}`}>
        <Search size={15} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label="Search CVE, package or host"
          aria-autocomplete="list"
          aria-expanded={panelOpen}
          aria-controls={LIST_ID}
          aria-activedescendant={panelOpen ? activeId : undefined}
          placeholder="Search CVE, package or host"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onInputKeyDown}
          className="flex-1 min-w-0 bg-transparent text-body text-ink placeholder:text-graphite outline-none border-0 p-0"
        />
        <kbd className="shrink-0 font-mono text-small leading-none px-1.5 py-0.5 rounded-badge border border-rule">/</kbd>
      </label>

      {panelOpen && (
        <div
          className="absolute left-0 top-[calc(100%+4px)] z-[3300] flex flex-col bg-sheet border border-rule rounded-panel shadow-overlay overflow-hidden"
          style={{ width: 'min(680px, calc(100vw - 96px))' }}
          onMouseDown={(event) => event.preventDefault()}
        >
          <div id={LIST_ID} role="listbox" aria-label="Search results" className="max-h-[min(560px,70vh)] overflow-y-auto py-1">
            {term.length < MIN_CHARS && <p className="px-3 py-2 text-body text-graphite">Type at least {MIN_CHARS} characters.</p>}

            {cveId && (
              <Link {...optionProps('gs-cve-jump', cvePath(cveId))}>
                <span className="w-[136px] shrink-0 truncate font-mono text-id font-semibold text-ink">{cveId}</span>
                {exactCve ? (
                  <>
                    <span className="w-[68px] shrink-0">
                      <SeverityBadge severity={exactCve.severity} />
                    </span>
                    <span className="flex-1 min-w-0 truncate text-graphite">{exactCve.description}</span>
                    <Meta>{plural(exactCve.affected_host_count, 'host')}</Meta>
                  </>
                ) : (
                  <span className="flex-1 min-w-0 truncate text-graphite">Open the CVE page</span>
                )}
              </Link>
            )}

            {groups.map(({ group, rows, total, error, loading }) => {
              if (!error && rows.length === 0) return null;
              const Row = ROWS[group.key];
              const headingId = `gs-heading-${group.key}`;
              return (
                <div key={group.key} role="group" aria-labelledby={headingId} className={loading && rows.length > 0 ? 'opacity-60' : ''}>
                  <div className="flex items-baseline justify-between gap-3 px-3 pt-2.5 pb-1 text-small text-graphite">
                    <span id={headingId} className="font-semibold">
                      {cveId ? 'Other matches' : group.title}
                    </span>
                    {!loading && !error && <span className="tabular-nums">{plural(total, 'match', 'matches')}</span>}
                  </div>
                  {error && (
                    <p className="flex items-center gap-2 h-9 px-3 text-body text-graphite">
                      Couldn't load {group.title.toLowerCase()}.
                      <button type="button" onClick={retry} className="text-signal hover:underline">
                        Try again
                      </button>
                    </p>
                  )}
                  {!error &&
                    rows.map((row) => {
                      const id = `gs-${group.key}-${group.rowId(row)}`;
                      if (loading) {
                        return (
                          <div key={id} aria-hidden="true" className="flex items-center gap-3 h-9 mx-1 px-2 text-body">
                            <Row row={row} term={term} />
                          </div>
                        );
                      }
                      return (
                        <Link key={id} {...optionProps(id, group.rowPath(row))}>
                          <Row row={row} term={term} />
                        </Link>
                      );
                    })}
                  {!error && !loading && !cveId && total > rows.length && (
                    <Link {...optionProps(`gs-${group.key}-all`, group.listPath(term))}>
                      <span className="flex-1 text-signal">Show all {plural(total, ...group.noun)}</span>
                    </Link>
                  )}
                </div>
              );
            })}

            {blank && <p className="px-3 py-2 text-body text-graphite">Searching…</p>}

            {nothingFound && (
              <div className="px-3 py-3">
                <p className="text-body text-ink">
                  Nothing matches <span className="font-mono text-id">{term}</span>.
                </p>
                <p className="mt-0.5 text-small text-graphite">Search looks at CVE IDs, package names and hostnames.</p>
              </div>
            )}
          </div>

          <footer className="shrink-0 flex items-center gap-4 h-8 px-3 border-t border-rule text-small text-graphite">
            <span className="flex-1" aria-live="polite">
              {searching && !blank ? 'Searching…' : ''}
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> to move
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>Enter</Kbd> to open
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>Esc</Kbd> to close
            </span>
          </footer>
        </div>
      )}
    </div>
  );
}
